/**
 * useCountryFilter Hook
 * 
 * Provides country filtering functionality for the dashboard.
 * Fetches available countries from API and manages selection state.
 * 
 * Usage:
 * ```tsx
 * const { selectedCountries, countriesParam, showFilter, toggleCountry } = useCountryFilter()
 * 
 * // Use countriesParam in API calls
 * const geojson = await assetService.getGeoJSON(countriesParam)
 * ```
 */
import { useEffect, useCallback, useState } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { useCountryConfigStore, type CountryInfo } from '@/stores/countryConfigStore'
import { assetService } from '@/services/portfolio'

interface UseCountryFilterReturn {
    // State
    selectedCountries: string[]
    availableCountries: CountryInfo[]
    isLoading: boolean
    error: string | null

    /** Show dropdown only for admin/analyst OR customer with multiple countries */
    showFilter: boolean

    /** Computed countries param for API calls (undefined = empty map) */
    countriesParam: string | undefined

    // Actions
    toggleCountry: (code: string) => void
    setSelectedCountries: (codes: string[]) => void
    selectAll: () => void
    clearSelection: () => void
    refresh: () => Promise<void>
}

export function useCountryFilter(): UseCountryFilterReturn {
    const user = useAuthStore((s) => s.user)
    const {
        selectedCountries,
        availableCountries: rawAvailableCountries,
        isLoaded,
        setSelectedCountries,
        toggleCountry,
        selectAll,
        clearSelection,
        loadAvailableCountries,
        initializeSelection
    } = useCountryConfigStore()

    // Ensure availableCountries is always an array (safety for rehydration)
    const availableCountries = Array.isArray(rawAvailableCountries) ? rawAvailableCountries : []

    const [isLoading, setIsLoading] = useState(!isLoaded)
    const [error, setError] = useState<string | null>(null)

    const fetchCountries = useCallback(async () => {
        // Skip if already loaded (data comes from store)
        if (isLoaded) {
            setIsLoading(false)
            return
        }

        setIsLoading(true)
        setError(null)

        try {
            const response = await assetService.getAvailableCountries()
            loadAvailableCountries(response.countries, response.default_country)

            // Initialize selection based on role
            if (user?.role) {
                initializeSelection(user.role)
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load countries')
        } finally {
            setIsLoading(false)
        }
    }, [isLoaded, user?.role, loadAvailableCountries, initializeSelection])

    // Fetch on mount
    useEffect(() => {
        fetchCountries()
    }, [fetchCountries])

    // Determine if filter should be shown
    // Always show for admin/analyst, show for others if they have multiple countries
    const isAdminOrAnalyst = user?.role === 'admin' || user?.role === 'analyst'
    const hasMultipleCountries = availableCountries.length > 1
    // Show filter immediately for admin/analyst, or if user has multiple countries
    const showFilter = isAdminOrAnalyst || hasMultipleCountries

    // Debug logging


    // Compute countries param for API
    const countriesParam = selectedCountries.length > 0
        ? selectedCountries.join(',')
        : undefined

    return {
        selectedCountries,
        availableCountries,
        isLoading,
        error,
        showFilter,
        countriesParam,
        toggleCountry,
        setSelectedCountries,
        selectAll,
        clearSelection,
        refresh: fetchCountries
    }
}
